/**
 * SSD Intranet Role & Access Control Utilities
 */

import { getLocalAdminSettings } from './adminSync';
import type { UserRole, MenuItem, UserRoleProfile } from '../types/roles';
import type { DirectoryEntry } from '../types/api';
import {
  DEFAULT_MENU_ITEMS,
  RESTRICTED_CATEGORY_RULES,
  DEFAULT_ROLE_MAPPINGS,
  AUTHORIZED_ADMIN_EMAILS,
} from '../config/rolePermissions';

/**
 * Determine role profile (roles & primary team) for a given email address
 */
export function resolveUserRoleProfile(email: string | undefined | null): UserRoleProfile {
  const normalizedEmail = (email || '').toLowerCase().trim();
  if (!normalizedEmail) {
    return { email: '', roles: ['Staff'], primaryTeam: '', assignedTeams: [] };
  }

  const adminSettings = getLocalAdminSettings();
  const adminMapped = adminSettings?.userMappings?.[normalizedEmail];
  const defaultMapped = DEFAULT_ROLE_MAPPINGS[normalizedEmail];
  const emp = adminSettings?.activeEmployees?.find(e => (e.email || '').toLowerCase().trim() === normalizedEmail);

  // 1. Determine Roles: Explicit admin override -> Default role mappings -> Employee Type heuristics -> Default Staff
  let roles: UserRole[] = ['Staff'];
  if (adminMapped?.roles && adminMapped.roles.length > 0) {
    roles = adminMapped.roles;
  } else if (defaultMapped?.roles && defaultMapped.roles.length > 0) {
    roles = defaultMapped.roles;
  } else if (emp) {
    const empType = (emp.employeeType || '').toLowerCase();
    if (empType.includes('admin') || empType.includes('superadmin')) {
      roles = ['Admin'];
    } else if (empType.includes('provider') || empType.includes('doctor') || empType.includes('md') || empType.includes('pa')) {
      roles = ['Provider'];
    }
  } else if (normalizedEmail.includes('admin') || normalizedEmail.includes('director')) {
    roles = ['Admin', 'Management'];
  } else if (normalizedEmail.includes('dr') || normalizedEmail.includes('provider') || normalizedEmail.includes('md')) {
    roles = ['Provider'];
  } else if (normalizedEmail.includes('manager')) {
    roles = ['Management'];
  }

  // 2. Determine Teams: Admin settings / Google Sheets 'Employee List' tab (via activeEmployees) is primary source of truth
  const deletedTeams = adminSettings?.deletedTeams || [];
  const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));

  let assignedTeams: string[] = [];
  if (adminMapped?.assignedTeams && Array.isArray(adminMapped.assignedTeams) && adminMapped.assignedTeams.length > 0) {
    assignedTeams = adminMapped.assignedTeams.filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && !deletedSet.has(t.toLowerCase().trim()));
  } else if (emp && emp.team) {
    assignedTeams = emp.team
      .split(',')
      .map(t => t.trim())
      .filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && !deletedSet.has(t.toLowerCase().trim()));
  } else if (adminMapped?.primaryTeam && adminMapped.primaryTeam.toLowerCase() !== 'no team' && adminMapped.primaryTeam.toLowerCase() !== 'management' && !deletedSet.has(adminMapped.primaryTeam.toLowerCase().trim())) {
    assignedTeams = [adminMapped.primaryTeam];
  } else if (defaultMapped?.assignedTeams && defaultMapped.assignedTeams.length > 0) {
    assignedTeams = defaultMapped.assignedTeams.filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && !deletedSet.has(t.toLowerCase().trim()));
  } else if (defaultMapped?.primaryTeam && defaultMapped.primaryTeam.toLowerCase() !== 'no team' && defaultMapped.primaryTeam.toLowerCase() !== 'management' && !deletedSet.has(defaultMapped.primaryTeam.toLowerCase().trim())) {
    assignedTeams = [defaultMapped.primaryTeam];
  }

  const primaryTeam = assignedTeams.length > 0 ? assignedTeams[0] : '';

  return {
    email: normalizedEmail,
    roles: Array.from(new Set(roles)),
    primaryTeam,
    assignedTeams: Array.from(new Set(assignedTeams)),
  };
}

/**
 * Filter menu items based on user roles
 */
export function filterMenuItemsForRoles(userRoles: UserRole[], menuItems: MenuItem[] = DEFAULT_MENU_ITEMS): MenuItem[] {
  return menuItems.filter(item => {
    // If no roles specified, accessible to all
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    // Check if user has at least one required role
    return item.allowedRoles.some(requiredRole => userRoles.includes(requiredRole));
  });
}

/**
 * Check if a category is allowed for the user's roles
 */
export function isCategoryAllowedForRoles(categoryName: string, userRoles: UserRole[]): boolean {
  const rule = RESTRICTED_CATEGORY_RULES.find(
    r => r.category.toLowerCase() === categoryName.toLowerCase()
  );

  // If no explicit restriction rule, category is accessible to everyone
  if (!rule) {
    return true;
  }

  // Check if user has at least one allowed role
  return rule.allowedRoles.some(role => userRoles.includes(role));
}

/**
 * Filter category names or category objects by user roles
 */
export function filterCategoriesForRoles<T extends string | { name: string }>(
  categories: T[],
  userRoles: UserRole[]
): T[] {
  return categories.filter(cat => {
    const catName = typeof cat === 'string' ? cat : cat.name;
    return isCategoryAllowedForRoles(catName, userRoles);
  });
}

/**
 * Explicit set of terminated employee email accounts extracted from Column B (2 Terminated) in Google Sheet
 */
export const TERMINATED_EMPLOYEE_EMAILS = new Set([
  'amayo@ssdspc.com',
  'mwiggins@ssdspc.com',
  'hnicholson@ssdspc.com',
  'ptaylor@ssdspc.com',
  'dyearwood@ssdspc.com',
  'acochran@ssdspc.com',
  'hdavlin@ssdspc.com',
  'mcdonald.r.megan@ssdspc.com',
  'lbaker@ssdspc.com',
  'ocarter@ssdspc.com',
  'tthompson@ssdspc.com',
  'rvelasquez@ssdspc.com',
  'arodriguez@ssdspc.com',
  'jpatrick@ssdspc.com',
  'ajoyner@ssdspc.com',
  'btuck@ssdspc.com',
  'kmatthews@ssdspc.com',
  'snix@ssdspc.com',
  'kmills@ssdspc.com',
  'hcook@ssdspc.com',
  'imoore@ssdspc.com',
  'medmondson@ssdspc.com',
  'khickmon@ssdspc.com',
  'kkennedy@ssdspc.com',
  'akeely@ssdspc.com',
  'jfields@ssdspc.com',
  'krna.lopz.0001@ssdspc.com',
  'cechols@ssdspc.com',
  'dmcgee@ssdspc.com',
  'lmiller@ssdspc.com',
]);

/**
 * Helper to check if an email represents an active individual employee account (@ssdspc.com)
 */
export function isIndividualActiveUserEmail(email: string, teamString: string = ''): boolean {
  const normEmail = email.toLowerCase().trim();
  const normTeam = teamString.toLowerCase().trim();

  // 1. MUST be an internal company account (@ssdspc.com) - Exclude external gmail, yahoo, etc.
  if (!normEmail.endsWith('@ssdspc.com')) {
    return false;
  }

  // 2. Exclude explicit terminated accounts (both hardcoded fallbacks and dynamic sheet sync)
  if (TERMINATED_EMPLOYEE_EMAILS.has(normEmail)) {
    return false;
  }
  try {
    const { terminatedEmails } = getLocalAdminSettings();
    if (terminatedEmails && Array.isArray(terminatedEmails)) {
      if (terminatedEmails.some(t => t.toLowerCase().trim() === normEmail)) {
        return false;
      }
    }
  } catch {}

  // 3. Exclude Terminated / Inactive / Former / Deleted accounts by keyword
  if (
    normTeam.includes('terminate') ||
    normTeam.includes('termed') ||
    normTeam.includes('inactive') ||
    normTeam.includes('former') ||
    normTeam.includes('deleted') ||
    normTeam.includes('ex-employee') ||
    normEmail.includes('terminate') ||
    normEmail.includes('termed') ||
    normEmail.includes('inactive') ||
    normEmail.includes('former')
  ) {
    return false;
  }

  // 4. Exclude Generic Chat Groups, Channels, & Distribution Lists
  const groupEmailKeywords = [
    'chat', 'group', 'channel', 'everyone', 'allstaff', 'all-staff',
    'department', 'dept', 'list@', 'dist@', 'distribution', 'subscribers',
    'nurses@', 'providers@', 'doctors@', 'reception@', 'support@',
    'help@', 'info@', 'contact@', 'dev@example.com'
  ];

  if (groupEmailKeywords.some(kw => normEmail.includes(kw) || normTeam.includes(kw))) {
    return false;
  }

  // Exclude generic team aliases
  const genericTeamAliases = ['billing@ssdspc.com', 'clinical@ssdspc.com', 'frontdesk@ssdspc.com', 'staff@ssdspc.com'];
  if (genericTeamAliases.includes(normEmail)) {
    return false;
  }

  return true;
}

/**
 * Extract all actual active user profiles dynamically from Google Sheet entries, Employee tab, & user mappings
 */
export function extractAllUserProfilesFromEntries(
  entries: DirectoryEntry[] = [],
  userMappings: Record<string, { roles: UserRole[]; primaryTeam: string; assignedTeams?: string[] }> = {}
): Array<{ email: string; name?: string; team: string; primaryTeam: string; roles: UserRole[]; assignedTeams: string[] }> {
  const userMap = new Map<string, { email: string; name?: string; teams: Set<string>; roles: Set<UserRole>; isExplicit: boolean }>();

  // 1. Seed with all dynamic active employees from the Employee tab
  try {
    const { activeEmployees } = getLocalAdminSettings();
    if (activeEmployees && Array.isArray(activeEmployees)) {
      activeEmployees.forEach(emp => {
        const email = (emp.email || '').toLowerCase().trim();
        if (!email || !isIndividualActiveUserEmail(email)) return;

        const empType = (emp.employeeType || '').toLowerCase();
        let defaultRole: UserRole = 'Staff';
        if (empType.includes('admin') || empType.includes('superadmin')) {
          defaultRole = 'Admin';
        } else if (empType.includes('provider') || empType.includes('doctor') || empType.includes('md') || empType.includes('pa')) {
          defaultRole = 'Provider';
        }

        const rawTeams = (emp.team || '').split(',')
          .map(t => t.trim())
          .filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management');

        userMap.set(email, {
          email,
          name: emp.name || emp.displayName,
          teams: new Set<string>(rawTeams),
          roles: new Set<UserRole>([defaultRole]),
          isExplicit: false,
        });
      });
    }
  } catch {}

  // 2. Overlay explicit user mappings from Admin matrix (Custom roles & explicit team assignments)
  Object.entries(userMappings).forEach(([email, config]) => {
    const norm = email.toLowerCase().trim();
    if (!norm) return;
    if (!isIndividualActiveUserEmail(norm, config.primaryTeam)) return;

    const existing = userMap.get(norm);
    if (existing) {
      if (config.roles && config.roles.length > 0) {
        existing.roles = new Set(config.roles);
      }
      if (config.assignedTeams && Array.isArray(config.assignedTeams)) {
        existing.teams = new Set(
          config.assignedTeams.filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management')
        );
      } else if (config.primaryTeam && config.primaryTeam.toLowerCase() !== 'no team' && config.primaryTeam.toLowerCase() !== 'management') {
        existing.teams = new Set([config.primaryTeam]);
      }
      existing.isExplicit = true;
    } else {
      const assigned = Array.isArray(config.assignedTeams) && config.assignedTeams.length > 0 
        ? config.assignedTeams.filter(t => t && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management')
        : (config.primaryTeam && config.primaryTeam.toLowerCase() !== 'no team' && config.primaryTeam.toLowerCase() !== 'management' ? [config.primaryTeam] : []);

      userMap.set(norm, {
        email: norm,
        name: undefined,
        teams: new Set(assigned),
        roles: new Set(config.roles || ['Staff']),
        isExplicit: true,
      });
    }
  });

  // 3. Discover any additional individual user accounts from entries that weren't in activeEmployees
  entries.forEach(entry => {
    if (!entry.accessEmails) return;

    entry.accessEmails.forEach(rawEmail => {
      const email = rawEmail.toLowerCase().trim();
      if (!email) return;
      if (!isIndividualActiveUserEmail(email)) return;

      if (!userMap.has(email)) {
        const profile = resolveUserRoleProfile(email);
        userMap.set(email, {
          email,
          name: undefined,
          teams: new Set<string>(profile.assignedTeams),
          roles: new Set<UserRole>(profile.roles),
          isExplicit: false,
        });
      }
    });
  });

  // 4. Always include core active domain accounts
  ['agonzalez@ssdspc.com', 'admin@ssdspc.com', 'mm@ssdspc.com', 'bcurren@ssdspc.com', 'automation@ssdspc.com'].forEach(email => {
    if (!userMap.has(email) && isIndividualActiveUserEmail(email)) {
      const profile = resolveUserRoleProfile(email);
      userMap.set(email, {
        email,
        name: undefined,
        teams: new Set(profile.assignedTeams),
        roles: new Set(profile.roles),
        isExplicit: false,
      });
    }
  });

  const adminSettings = getLocalAdminSettings();
  const deletedTeams = adminSettings?.deletedTeams || [];
  const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));

  // Convert to array and format team strings, strictly filtering out any terminated accounts
  return Array.from(userMap.values())
    .filter(u => isIndividualActiveUserEmail(u.email))
    .map(u => {
      const teamsArr = Array.from(u.teams).filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'no team' && t.toLowerCase() !== 'management' && !deletedSet.has(t.toLowerCase().trim()));
      const primaryTeam = teamsArr.length > 0 ? teamsArr[0] : '';
      const name = u.name || getUserDisplayName({ email: u.email });
      return {
        email: u.email,
        name,
        team: primaryTeam,
        primaryTeam,
        roles: Array.from(u.roles),
        assignedTeams: teamsArr,
      };
    })
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
}

/**
 * Helper to resolve a clean, human-readable display name for any employee account
 */
export function getUserDisplayName(user: { email: string; name?: string } | null | undefined): string {
  if (!user || !user.email) return 'User Account';
  if (user.name && user.name.trim() && !user.name.includes('@')) {
    return user.name.trim();
  }

  const email = user.email.toLowerCase().trim();

  // Try to use the dynamic employee directory from the Master Index sheet first
  try {
    const { employeeDirectory, activeEmployees } = getLocalAdminSettings();
    if (employeeDirectory && employeeDirectory[email]) {
      return employeeDirectory[email];
    }
    if (activeEmployees && Array.isArray(activeEmployees)) {
      const found = activeEmployees.find(e => e.email.toLowerCase() === email);
      if (found && (found.name || found.displayName)) {
        return found.name || found.displayName || '';
      }
    }
  } catch {}

  const prefix = email.split('@')[0];

  const knownNameMap: Record<string, string> = {
    'agonzalez': 'Alvaro Gonzalez',
    'admin': 'System Administrator',
    'mm': 'MM Management',
    'bcurren': 'B. Curren',
    'automation': 'Automation Service',
    'frontdesk': 'Front Desk Team',
    'billing': 'Billing Team',
    'clinical': 'Clinical Team',
    'surgery': 'Surgery Team',
    'team_billingteam': 'Billing Team',
    'team_frontdesk': 'Front Desk Team',
    'team_clinical': 'Clinical Team',
    'team_superadmin': 'SuperAdmin Team',
    'team_management': 'Management Team',
  };

  if (knownNameMap[prefix]) {
    return knownNameMap[prefix];
  }

  return prefix
    .replace(/[._-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Check if a user has explicit access to a directory entry or sheet in Column 3 (All Sheets)
 * Strictly enforces that SuperAdmin / restricted sheets are only visible to users with explicit access.
 */
export function hasUserAccessToEntry(
  entry: DirectoryEntry | null | undefined,
  userEmail: string | null | undefined,
  userRoles: UserRole[] = ['Staff'],
  userAssignedTeams: string[] = []
): boolean {
  if (!entry) return false;
  const normEmail = (userEmail || '').toLowerCase().trim();
  if (!normEmail) return false;

  // 1. SuperAdmin / System Owner Accounts have master visibility
  const isOwnerAdmin =
    AUTHORIZED_ADMIN_EMAILS.some((e) => e.toLowerCase() === normEmail) ||
    normEmail === 'agonzalez@ssdspc.com' ||
    normEmail === 'admin@ssdspc.com' ||
    normEmail === 'mm@ssdspc.com';

  if (isOwnerAdmin) {
    return true;
  }

  // 2. Role-based category gate
  const categories = Array.isArray(entry.categories) ? entry.categories : [];
  if (categories.length > 0) {
    if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) {
      return false;
    }
  }

  const accessList = Array.isArray(entry.accessEmails)
    ? entry.accessEmails.map((e) => e.toLowerCase().trim()).filter(Boolean)
    : [];
  const teamLower = (entry.team || '').toLowerCase();
  const isSuperAdminSheet =
    teamLower.includes('superadmin') ||
    categories.some((c) => c.toLowerCase().includes('superadmin'));

  // 3. If it is a SuperAdmin sheet / team:
  // ONLY show if user is explicitly listed in entry.accessEmails or domain-wide access is enabled
  if (isSuperAdminSheet) {
    if (accessList.length > 0) {
      return (
        accessList.includes(normEmail) ||
        accessList.includes('domain:ssdspc.com') ||
        accessList.includes('anyone')
      );
    }
    // If no explicit access list is present on a SuperAdmin sheet, hide from non-owners
    return false;
  }

  // 4. For standard sheets with an explicit access list specified:
  if (accessList.length > 0) {
    const hasEmailMatch = accessList.includes(normEmail);
    const hasDomainMatch =
      accessList.includes('domain:ssdspc.com') || accessList.includes('anyone');
    const hasTeamMatch = userAssignedTeams.some((t) =>
      accessList.includes(t.toLowerCase().trim())
    );
    return hasEmailMatch || hasDomainMatch || hasTeamMatch;
  }

  // 5. Unrestricted standard sheet
  return true;
}

/**
 * Check if a user has explicit access to a daily task sheet or file in Column 1 (Daily Tasks)
 * Strictly verifies permissions so each user only sees the tasks/files they have access to.
 */
export function hasUserAccessToDailyTask(
  entry: DirectoryEntry | null | undefined,
  userEmail: string | null | undefined,
  userRoles: UserRole[] = ['Staff'],
  userAssignedTeams: string[] = []
): boolean {
  if (!entry) return false;
  const normEmail = (userEmail || '').toLowerCase().trim();
  if (!normEmail) return false;

  // 1. SuperAdmin / System Owner Accounts have master visibility
  const isOwnerAdmin =
    AUTHORIZED_ADMIN_EMAILS.some((e) => e.toLowerCase() === normEmail) ||
    normEmail === 'agonzalez@ssdspc.com' ||
    normEmail === 'admin@ssdspc.com' ||
    normEmail === 'mm@ssdspc.com';

  if (isOwnerAdmin) {
    return true;
  }

  // 2. Role-based category gate
  const categories = Array.isArray(entry.categories) ? entry.categories : [];
  if (categories.length > 0) {
    if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) {
      return false;
    }
  }

  const accessList = Array.isArray(entry.accessEmails)
    ? entry.accessEmails.map((e) => e.toLowerCase().trim()).filter(Boolean)
    : [];

  if (accessList.length > 0) {
    const hasEmailMatch = accessList.includes(normEmail);
    const hasDomainMatch =
      accessList.includes('domain:ssdspc.com') || accessList.includes('anyone');
    const hasTeamMatch = userAssignedTeams.some((t) =>
      accessList.includes(t.toLowerCase().trim())
    );
    if (hasEmailMatch || hasDomainMatch || hasTeamMatch) {
      return true;
    }
  }

  // 3. Check assignee name in description against user's display name or email prefix
  const desc = (entry.description || '').toLowerCase();
  const emailPrefix = normEmail.split('@')[0];
  if (emailPrefix && desc.includes(emailPrefix)) {
    return true;
  }

  return false;
}
